<template>
  <div class="space-y-2">
    <div class="flex items-center justify-between">
      <h4 class="text-sm font-semibold">Historique</h4>
      <span class="text-xs text-muted-foreground">{{ events.length }} évènement{{ events.length > 1 ? "s" : "" }}</span>
    </div>

    <p v-if="!events.length" class="text-xs text-muted-foreground">Aucun évènement.</p>

    <ol v-else class="relative space-y-2.5 border-l border-border/70 pl-4">
      <li v-for="event in visibleEvents" :key="event.key" class="relative">
        <span class="absolute -left-[21px] top-1 flex h-3 w-3 items-center justify-center rounded-full ring-2 ring-card"
              :class="event.dotClass"></span>
        <div class="flex flex-wrap items-baseline gap-x-1.5 text-xs">
          <span class="font-medium text-foreground">{{ event.author }}</span>
          <span class="text-muted-foreground" v-html="event.label"></span>
          <span class="ml-auto shrink-0 text-[11px] text-muted-foreground" :title="formatFull(event.date)">
            {{ formatRelative(event.date) }}
          </span>
        </div>
        <p v-if="event.excerpt" class="mt-0.5 line-clamp-2 rounded-md bg-muted/50 px-2 py-1 text-xs text-muted-foreground">
          {{ event.excerpt }}
        </p>
      </li>
    </ol>

    <button
      v-if="events.length > collapsedCount"
      type="button"
      class="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      @click="expanded = !expanded"
    >
      {{ expanded ? "Réduire" : `Voir les ${events.length - collapsedCount} évènements plus anciens` }}
    </button>
  </div>
</template>

<script>
import { ISSUE_PRIORITIES, ISSUE_STATUSES } from "@/composables/boardService";

// Champs suivis dans l'historique — les autres (position, timestamps techniques) sont ignorés.
const FIELD_LABELS = {
  title: "le titre",
  description: "la description",
  status: "le statut",
  priority: "la priorité",
  owner_id: "l'assignation",
  due_date: "l'échéance",
  archived_at: "l'archivage",
};

export default {
  name: "IssueHistory",
  props: {
    issue: { type: Object, required: true },
    users: { type: Array, default: () => [] },
    collapsedCount: { type: Number, default: 6 },
  },
  data() {
    return { expanded: false };
  },
  computed: {
    events() {
      return [
        ...this.creationEvents(),
        ...this.logEvents(),
        ...this.commentEvents(),
        ...this.attachmentEvents(),
      ]
        .filter((event) => event.date instanceof Date && !isNaN(event.date))
        .sort((a, b) => b.date - a.date);
    },
    visibleEvents() {
      return this.expanded ? this.events : this.events.slice(0, this.collapsedCount);
    },
  },
  watch: {
    "issue.id"() {
      this.expanded = false;
    },
  },
  methods: {
    creationEvents() {
      if (!this.issue.created_at) return [];
      return [{
        key: "created",
        date: new Date(this.issue.created_at),
        author: this.userName(this.issue.creator),
        label: "a créé la carte",
        dotClass: "bg-emerald-500",
      }];
    },
    logEvents() {
      const events = [];
      for (const log of this.issue.logs || []) {
        // La création est déjà couverte par l'évènement dédié.
        if (log.description === "created") continue;
        const attributes = (log.properties && log.properties.attributes) || {};
        const old = (log.properties && log.properties.old) || {};
        for (const field of Object.keys(attributes)) {
          if (!FIELD_LABELS[field]) continue;
          const label = this.changeLabel(field, old[field], attributes[field]);
          if (!label) continue;
          events.push({
            key: `log-${log.id}-${field}`,
            date: new Date(log.created_at),
            author: this.logAuthor(log),
            label,
            dotClass: field === "status" ? "bg-sky-500" : "bg-gray-400",
          });
        }
      }
      return events;
    },
    commentEvents() {
      return (this.issue.comments || []).map((comment) => ({
        key: `comment-${comment.id}`,
        date: new Date(comment.created_at),
        author: this.userName(comment.user),
        label: "a commenté",
        excerpt: this.excerpt(comment.body),
        dotClass: "bg-indigo-400",
      }));
    },
    attachmentEvents() {
      return (this.issue.attachments || []).map((attachment) => ({
        key: `attachment-${attachment.id}`,
        date: new Date(attachment.created_at),
        author: this.userName(attachment.user),
        label: `a ajouté la pièce jointe <strong>${this.escape(attachment.filename || "")}</strong>`,
        dotClass: "bg-yellow-500",
      }));
    },
    /**
     * Phrase décrivant un changement de champ. Retourne null quand la valeur n'a pas
     * réellement bougé (le journal enregistre parfois des écritures identiques).
     */
    changeLabel(field, from, to) {
      if (String(from ?? "") === String(to ?? "")) return null;

      if (field === "status") {
        return `a déplacé la carte de <strong>${this.statusLabel(from)}</strong> vers <strong>${this.statusLabel(to)}</strong>`;
      }
      if (field === "title") {
        return `a renommé la carte en <strong>${this.escape(to || "")}</strong>`;
      }
      if (field === "description") {
        return to ? "a modifié la description" : "a supprimé la description";
      }
      if (field === "owner_id") {
        return to
          ? `a assigné la carte à <strong>${this.escape(this.userNameById(to) || `#${to}`)}</strong>`
          : "a retiré l'assignation";
      }
      if (field === "priority") {
        // null et 0 valent tous deux « Aucune » : pas de changement à afficher.
        if (this.priorityLabel(from) === this.priorityLabel(to)) return null;
        return `a mis la priorité sur <strong>${this.priorityLabel(to)}</strong>`;
      }
      if (field === "due_date") {
        return to ? `a fixé l'échéance au <strong>${this.formatDay(to)}</strong>` : "a retiré l'échéance";
      }
      if (field === "archived_at") {
        return to ? "a archivé la carte" : "a désarchivé la carte";
      }

      return `a modifié ${FIELD_LABELS[field]}`;
    },
    statusLabel(id) {
      const status = ISSUE_STATUSES.find((s) => s.id === id);
      return status ? status.label : (id || "—");
    },
    priorityLabel(value) {
      const priority = ISSUE_PRIORITIES.find((p) => p.id === Number(value));
      return priority ? priority.label : String(value ?? "—");
    },
    userName(user) {
      if (!user) return "Système";
      return user.name || user.email || "Système";
    },
    userNameById(id) {
      const user = this.users.find((u) => String(u.id) === String(id));
      return user ? this.userName(user) : null;
    },
    /**
     * Le journal d'activité n'a longtemps enregistré que `causer_id` sans son type,
     * ce qui laissait la relation `causer` vide : on retombe sur la liste des
     * utilisateurs pour retrouver l'auteur des entrées historiques.
     */
    logAuthor(log) {
      if (log.causer) return this.userName(log.causer);
      return this.userNameById(log.causer_id) || "Système";
    },
    excerpt(body) {
      const text = String(body || "").replace(/[#*_>`]/g, "").trim();
      return text.length > 160 ? `${text.slice(0, 160)}…` : text;
    },
    escape(text) {
      return String(text).replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[char]));
    },
    formatDay(value) {
      return value ? new Date(value).toLocaleDateString("fr-FR") : "";
    },
    formatFull(date) {
      return date ? date.toLocaleString("fr-FR") : "";
    },
    formatRelative(date) {
      if (!date) return "";
      const seconds = Math.round((Date.now() - date.getTime()) / 1000);
      if (seconds < 60) return "à l'instant";
      const minutes = Math.round(seconds / 60);
      if (minutes < 60) return `il y a ${minutes} min`;
      const hours = Math.round(minutes / 60);
      if (hours < 24) return `il y a ${hours} h`;
      const days = Math.round(hours / 24);
      if (days < 7) return `il y a ${days} j`;
      return date.toLocaleDateString("fr-FR");
    },
  },
};
</script>
